import React, { ComponentType } from "react";

import { IStoreState } from "@montana/store/store";
import { isTest } from "@montana/helpers/browser.helper";

import { ITranslationsContainerState } from "./translations";
import { generateContainer } from "../base/generate-container";
import { generateBaseContainer, IBaseContainerProps } from "../base/base.container";

const mapState = (state: IStoreState): ITranslationsContainerState => ({
    ...state.translations,
});

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ITranslations = any;

interface IWithGetTranslation {
    getTranslation: (mapFn: (translations: ITranslations) => string, defaultValue: string) => string;
}

export type ITranslationsContainerProps = ITranslationsContainerState & IWithGetTranslation;

export type translationsSelector = string | ((translations: ITranslations) => string);

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type

export const makeGetTranslation = ({ translations }: ITranslationsContainerState) =>
    (selector: translationsSelector, defaultValue = ""): string => {
        if (typeof selector === "string") {
            defaultValue = defaultValue || selector;
        }

        try {
            if (Object.keys(translations).length === 0) {
                return defaultValue;
            }

            const t = typeof selector === "function"
                ? selector(translations)
                : selector.split(".").reduce<string>((o: any, i: any) => o[i], translations as any);

            if (t === undefined) {
                throw new Error("Translation undefined");
            }

            return t;
        } catch (e) {
            if (!isTest) {
                // eslint-disable-next-line no-console
                // console.warn(`Could not find translation. Using default value (${defaultValue})`, e); shhh
            }

            return `${defaultValue}`;
        }
    };

function withGetTranslation<TOwnProps = {}>(
    Component: ComponentType<ITranslationsContainerProps>,
): React.FC<ITranslationsContainerState & TOwnProps> {
    return (props): JSX.Element => {
        const getTranslation = makeGetTranslation(props);
        const newProps: ITranslationsContainerProps = { ...props, getTranslation };

        return <Component {...newProps} />;
    };
}

const [, connector, translateHook] = generateContainer(mapState);

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function connectWithTranslations<TOwnProps>(comp: ComponentType<ITranslationsContainerProps & TOwnProps>) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return connector<TOwnProps>(withGetTranslation(comp as any));
}

export const TranslationsContainer = connectWithTranslations<IBaseContainerProps<ITranslationsContainerProps>>(generateBaseContainer());

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function useTranslations() {
    const translationsContainer = translateHook();
    const translate = makeGetTranslation(translationsContainer);

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    return (selector: translationsSelector, defaultValue?: string) => translate(selector, defaultValue);
}
